import { AwsRum, AwsRumConfig } from "aws-rum-web";
import { RumConfig } from "@amzn/afxtaco-types";
import { logger } from "@amzn/afxtaco-utilities";
import { fetchAuthSession } from "aws-amplify/auth";
import { shouldIgnoreError } from "./rumErrorFilter";

let rumClient: AwsRum | null = null;

/**
 * Initializes CloudWatch RUM using the Cognito Identity Pool for credentials.
 * No-ops gracefully if config is missing (personal stages).
 */
export function initializeRum(rumConfig: RumConfig): AwsRum | null {
  if (rumClient) return rumClient;

  try {
    const awsRumConfig: AwsRumConfig = {
      sessionSampleRate: 1,
      endpoint: `https://dataplane.rum.${rumConfig.region}.amazonaws.com`,
      telemetries: [
        ["errors", { ignore: shouldIgnoreError }],
        "performance",
        [
          "http",
          {
            urlsToInclude: [/\/api\//, /\/graphql/],
            recordAllRequests: true,
          },
        ],
      ],
      allowCookies: true,
      enableXRay: true,
      disableAutoPageView: true,
    };

    rumClient = new AwsRum(
      rumConfig.appMonitorId,
      "1.0.0",
      rumConfig.region,
      awsRumConfig,
    );

    // Provide credentials from the authenticated Amplify session
    rumClient.setAwsCredentials(async () => {
      const session = await fetchAuthSession();
      const credentials = session.credentials;
      if (!credentials) {
        throw new Error("No credentials in auth session");
      }
      return {
        accessKeyId: credentials.accessKeyId,
        secretAccessKey: credentials.secretAccessKey,
        sessionToken: credentials.sessionToken,
        expiration: credentials.expiration,
      };
    });

    logger.info({ loggerMsg: "CloudWatch RUM initialized" });
    return rumClient;
  } catch (error) {
    logger.error({
      loggerMsg: "Failed to initialize CloudWatch RUM",
      error,
    });
    return null;
  }
}

/**
 * Records a manual page view. Call on route changes.
 */
export function recordPageView(pageId: string): void {
  try {
    rumClient?.recordPageView(pageId);
  } catch (error) {
    logger.error({ loggerMsg: "Error recording RUM page view", error });
  }
}

/**
 * Records a custom event for business metrics.
 */
export function recordEvent(
  eventType: string,
  data: Record<string, unknown>,
): void {
  try {
    rumClient?.recordEvent(eventType, data);
  } catch (error) {
    logger.error({ loggerMsg: "Error recording RUM event", error });
  }
}

/**
 * Returns the raw AwsRum client (for advanced use cases).
 */
export function getRumClient(): AwsRum | null {
  return rumClient;
}
